import { GatewayAttachmentStatus } from "@/pages/mcp/gateway/GatewayAttachmentStatus";
import { useGatewayCreation } from "@/pages/mcp/gateway/useGatewayCreation";
import { CodeBlock } from "@/components/code";
import { FormPage } from "@/components/page-templates";
import { Input } from "@/components/ui/Input";
import { Text } from "@/components/ui/Text";
import { mcpServerRouteParam, tunneledMcpRouteParam } from "@/lib/sources";
import { useRoutes } from "@/routes";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Stack } from "@/components/ui/Stack";
import type { McpServer } from "@gram/client/models/components/mcpserver.js";
import type { TunneledMcpServer } from "@gram/client/models/components/tunneledmcpserver.js";
import { AlertCircle, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { NewServerGuardrailSection } from "@/pages/security/server-guardrails/NewServerGuardrailSection";
import {
  guardrailFailureMessage,
  useNewServerGuardrail,
  type NewServerGuardrailOutcome,
} from "@/pages/security/server-guardrails/useNewServerGuardrail";
import { RESOURCE_IDENTIFIER_EXPLAINER } from "./copy";
import { useCreateTunneledMcpSource } from "./hooks";
import { TunneledMcpSetupTabs } from "./TunneledMcpSetupTabs";
import { UserSessionIssuerSelect } from "@/components/user-session-issuer-select";
import {
  PROJECT_SPECIFIC_ISSUER_VALUE,
  defaultCreationUserSessionIssuerValue,
} from "@/components/user-session-issuer-select.utils";
import { useEffectiveUserSessionIssuers } from "@/hooks/useEffectiveUserSessionIssuers";

function validateDisplayName(value: string): string | null {
  if (!value.trim()) return "Display name is required";
  return null;
}

type CreatedState = {
  tunneledMcpServer: TunneledMcpServer;
  tunnelKey: string;
  mcpServer: McpServer;
};

export default function CreateTunneledMcp(): JSX.Element {
  const flow = useGatewayCreation();
  const creationLocked = flow.createdServerId !== null || flow.isAttaching;
  const routes = useRoutes();
  const createSource = useCreateTunneledMcpSource();
  const issuerQuery = useEffectiveUserSessionIssuers();
  const newGuardrail = useNewServerGuardrail();
  const [guardrailOutcome, setGuardrailOutcome] =
    useState<NewServerGuardrailOutcome | null>(null);
  const [name, setName] = useState("");
  const [resourceIdentifier, setResourceIdentifier] = useState("");
  const [touched, setTouched] = useState(false);
  const [created, setCreated] = useState<CreatedState | null>(null);
  const [issuerSelection, setIssuerSelection] = useState<string | null>(null);

  const defaultIssuerSelection = defaultCreationUserSessionIssuerValue(
    issuerQuery.organizationIssuers,
  );
  const selectedIssuer = issuerSelection ?? defaultIssuerSelection;

  const validationError = touched ? validateDisplayName(name) : null;
  const submitDisabled =
    creationLocked ||
    createSource.isPending ||
    validateDisplayName(name) !== null ||
    !newGuardrail.validation.ok ||
    issuerQuery.isLoading ||
    issuerQuery.isError ||
    selectedIssuer === "";

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (creationLocked) return;
    setTouched(true);
    if (validateDisplayName(name) !== null) return;
    if (!newGuardrail.validation.ok) return;

    try {
      const result = await createSource.mutateAsync({
        name: name.trim(),
        resourceIdentifier: resourceIdentifier.trim() || undefined,
        userSessionIssuerId:
          selectedIssuer === PROJECT_SPECIFIC_ISSUER_VALUE
            ? undefined
            : selectedIssuer,
      });
      setCreated(result);
      toast.success("Tunneled MCP server added");
      // Attempted only now that the server exists; a failure keeps the server
      // and is reported on the confirmation screen.
      const outcome = await newGuardrail.createFor(result.mcpServer);
      setGuardrailOutcome(outcome);
      if (outcome.status === "failed") {
        toast.error(guardrailFailureMessage(outcome), { duration: 12000 });
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to add tunneled MCP server";
      toast.error(message);
    }
  };

  if (created) {
    return (
      <FormPage
        scope="mcp:write"
        width="wide"
        title="Tunneled MCP server added"
        description="Use this tunnel key to connect an MCP server running in your own network."
      >
        <Stack gap={6}>
          <GatewayAttachmentStatus flow={flow} />
          {guardrailOutcome?.status === "failed" ? (
            <Stack gap={2}>
              <Alert variant="error" dismissible={false}>
                {guardrailFailureMessage(guardrailOutcome)}
              </Alert>
              <div>
                <Button
                  variant="secondary"
                  onClick={() =>
                    routes.mcp.details.guardrails.goTo(
                      mcpServerRouteParam(created.mcpServer),
                    )
                  }
                >
                  <Button.Text>Open Guardrails</Button.Text>
                </Button>
              </div>
            </Stack>
          ) : null}
          {guardrailOutcome?.status === "created" ? (
            <Alert variant="success" dismissible={false}>
              Guardrail &quot;{guardrailOutcome.name}&quot; created. Review it
              under the server&apos;s Guardrails tab.
            </Alert>
          ) : null}
          <div className="border p-5">
            <Text variant="subheading" className="mb-3">
              Tunnel key
            </Text>
            <Stack gap={3}>
              <Alert variant="warning" dismissible={false}>
                This key is only shown once. Copy it now and store it securely —
                you will need to rotate the key to get a new one.
              </Alert>
              <CodeBlock language="text">{created.tunnelKey}</CodeBlock>
            </Stack>
          </div>

          <TunneledMcpSetupTabs
            tunnelKey={created.tunnelKey}
            serverName={created.tunneledMcpServer.name}
          />

          <Stack direction="horizontal" gap={2}>
            {flow.gatewayId ? (
              <>
                <Button
                  variant="primary"
                  disabled={flow.isAttaching}
                  onClick={() => {
                    // The one-time key must be copied before leaving this screen.
                    void flow.complete(created.mcpServer.id).catch(() => {});
                  }}
                >
                  <Button.Text>Add to gateway</Button.Text>
                </Button>
                <Button
                  variant="secondary"
                  disabled={flow.isAttaching}
                  onClick={() => {
                    flow.cancel();
                  }}
                >
                  <Button.Text>Cancel</Button.Text>
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="primary"
                  onClick={() =>
                    routes.mcp.details.overview.goTo(
                      mcpServerRouteParam(created.mcpServer),
                    )
                  }
                >
                  <Button.Text>Open MCP server</Button.Text>
                </Button>
                <Button
                  variant="secondary"
                  onClick={() =>
                    routes.mcp.details.overview.goTo(
                      tunneledMcpRouteParam(created.tunneledMcpServer),
                    )
                  }
                >
                  <Button.Text>View source</Button.Text>
                </Button>
              </>
            )}
          </Stack>
        </Stack>
      </FormPage>
    );
  }

  return (
    <FormPage
      scope="mcp:write"
      title="New tunneled MCP server"
      description="Register an MCP server that runs in your private network and connects outbound to Speakeasy through a tunnel."
    >
      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        noValidate
      >
        <Stack gap={4}>
          <fieldset disabled={creationLocked} className="contents">
            <Stack gap={1}>
              <label
                htmlFor="tunneled-mcp-name"
                className="text-sm leading-none font-medium"
              >
                Display name
              </label>
              <Input
                id="tunneled-mcp-name"
                autoFocus
                placeholder="Internal MCP server"
                value={name}
                onChange={(value) => {
                  setName(value);
                  if (!touched) setTouched(true);
                }}
                onBlur={() => setTouched(true)}
                aria-invalid={validationError ? true : undefined}
                aria-describedby={
                  validationError ? "tunneled-mcp-name-error" : undefined
                }
              />
              {validationError && (
                <div
                  id="tunneled-mcp-name-error"
                  role="alert"
                  className="text-destructive mt-2 flex items-center gap-1.5 text-xs"
                >
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  <span>{validationError}</span>
                </div>
              )}
            </Stack>

            <Stack gap={1}>
              <label
                htmlFor="tunneled-mcp-resource-identifier"
                className="text-sm leading-none font-medium"
              >
                Resource identifier{" "}
                <span className="text-muted-foreground font-normal">
                  (optional)
                </span>
              </label>
              <Input
                id="tunneled-mcp-resource-identifier"
                placeholder="https://mcp.internal.example.com/mcp"
                value={resourceIdentifier}
                onChange={(value) => setResourceIdentifier(value)}
              />
              <Text muted small>
                {`${RESOURCE_IDENTIFIER_EXPLAINER} Leave blank if unknown — it can be recorded later in the source settings.`}
              </Text>
            </Stack>

            <Stack gap={1}>
              <label className="text-sm leading-none font-medium">
                Transport
              </label>
              <Text muted small>
                Outbound tunnel to a normal MCP server
              </Text>
            </Stack>

            <Stack gap={1}>
              <label className="text-sm leading-none font-medium">
                User session issuer
              </label>
              <UserSessionIssuerSelect
                issuers={issuerQuery.organizationIssuers}
                value={selectedIssuer}
                onValueChange={setIssuerSelection}
                includeProjectSpecific
                disabled={issuerQuery.isLoading || issuerQuery.isError}
              />
              <Text muted small>
                Organization issuers are shared across projects. Creating a
                project-specific issuer is available for exceptional setups.
              </Text>
              {issuerQuery.isError ? (
                <Alert variant="error" dismissible={false}>
                  Failed to load organization user session issuers.
                </Alert>
              ) : null}
            </Stack>
          </fieldset>
          <NewServerGuardrailSection
            guardrail={newGuardrail}
            serverName={name.trim() || "this server"}
            disabled={creationLocked || createSource.isPending}
          />
          <GatewayAttachmentStatus flow={flow} />
          {createSource.isError && (
            <Alert variant="error" dismissible={false}>
              {createSource.error.message}
            </Alert>
          )}

          <Stack direction="horizontal" gap={2}>
            <Button type="submit" variant="primary" disabled={submitDisabled}>
              {createSource.isPending ? (
                <Button.LeftIcon>
                  <Loader2 className="size-4 animate-spin" />
                </Button.LeftIcon>
              ) : null}
              <Button.Text>
                {createSource.isPending ? "Adding" : "Add server"}
              </Button.Text>
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={createSource.isPending || flow.isAttaching}
              onClick={() => {
                if (!flow.cancel()) routes.mcp.add.goTo();
              }}
            >
              <Button.Text>Cancel</Button.Text>
            </Button>
          </Stack>
        </Stack>
      </form>
    </FormPage>
  );
}
